import React, { useEffect, useMemo, useState } from "react";
import { Bell, Loader2, SendHorizonal } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { sendAdminNotification } from "@/api/admin";
import { useToast } from "@/components/ui/use-toast";
import OneSignalMigrationPanel from "./OneSignalMigrationPanel";

interface AdminUser {
  id: string;
  name: string | null;
  email: string;
}

const NotificationPanel: React.FC = () => {
  const { toast } = useToast();
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [targetMode, setTargetMode] = useState<"all" | "selected">("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const allSelected = useMemo(
    () => users.length > 0 && selectedIds.length === users.length,
    [users.length, selectedIds.length]
  );

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("id, name, email")
          .order("created_at", { ascending: false })
          .limit(300);

        if (error) throw error;
        setUsers((data as AdminUser[]) || []);
      } catch {
        toast({
          title: "Hata",
          description: "Kullanıcı listesi yüklenemedi",
          variant: "destructive",
        });
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, [toast]);

  const toggleUser = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      return;
    }
    setSelectedIds((prev) => prev.filter((currentId) => currentId !== id));
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(users.map((user) => user.id));
      return;
    }
    setSelectedIds([]);
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setDeepLink("");
    setTargetMode("all");
    setSelectedIds([]);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast({
        title: "Eksik bilgi",
        description: "Başlık ve mesaj alanları zorunludur",
        variant: "destructive",
      });
      return;
    }

    if (targetMode === "selected" && selectedIds.length === 0) {
      toast({
        title: "Alıcı seçimi gerekli",
        description: "En az bir kullanıcı seçmelisiniz",
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const result = await sendAdminNotification({
        title: title.trim(),
        body: body.trim(),
        targetMode,
        targetUserIds: targetMode === "selected" ? selectedIds : undefined,
        data: deepLink.trim() ? { deepLink: deepLink.trim() } : {},
      });

      if (!result.success) {
        toast({
          title: "Bildirim gönderilemedi",
          description: result.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Bildirim gönderildi",
        description:
          `${result.insertedCount || 0} kullanıcı için uygulama içi bildirim oluşturuldu` +
          (result.pushSent ? " ve OneSignal push gönderildi." : "."),
      });
      resetForm();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Bildirim Yönetimi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <label className="text-sm font-medium">Başlık</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Örn: Sistem duyurusu"
              maxLength={120}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium">Mesaj</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Göndermek istediğiniz mesaj"
              rows={4}
              maxLength={500}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium">Deep Link (Opsiyonel)</label>
            <Input
              value={deepLink}
              onChange={(e) => setDeepLink(e.target.value)}
              placeholder="/checks veya /project/123"
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium">Alıcı Grubu</label>
            <Select value={targetMode} onValueChange={(value: "all" | "selected") => setTargetMode(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm kullanıcılar</SelectItem>
                <SelectItem value="selected">Seçili kullanıcılar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {targetMode === "selected" && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Kullanıcı Seçimi</p>
                <div className="flex items-center gap-2">
                  <Checkbox checked={allSelected} onCheckedChange={(checked) => toggleSelectAll(Boolean(checked))} />
                  <span className="text-sm text-muted-foreground">Tümünü seç</span>
                </div>
              </div>
              <div className="max-h-56 space-y-2 overflow-auto pr-1">
                {loadingUsers ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Kullanıcılar yükleniyor...
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Kullanıcı bulunamadı</p>
                ) : (
                  users.map((user) => (
                    <label key={user.id} className="flex cursor-pointer items-center gap-2 rounded-md border p-2">
                      <Checkbox
                        checked={selectedIds.includes(user.id)}
                        onCheckedChange={(checked) => toggleUser(user.id, Boolean(checked))}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{user.name || "İsimsiz kullanıcı"}</p>
                        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">Seçilen kullanıcı sayısı: {selectedIds.length}</p>
            </div>
          )}

          <Button onClick={handleSend} disabled={sending || loadingUsers} className="w-full">
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Gönderiliyor...
              </>
            ) : (
              <>
                <SendHorizonal className="mr-2 h-4 w-4" />
                Bildirimi Gönder
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <OneSignalMigrationPanel />
    </div>
  );
};

export default NotificationPanel;